
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/auth';
import { useOrganizationData } from './useOrganizationData';

export function useSubscriptionStatus() {
  // Initial state is null to prevent flashing
  const [isSubscriptionActive, setIsSubscriptionActive] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { currentOrganization } = useAuth();
  const { organizationData, daysRemaining, isTrialActive } = useOrganizationData();
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      setIsLoading(true);
      
      try {
        // Vérifier si l'organisation a des données
        if (!currentOrganization && !organizationData) {
          console.log("Aucune organisation trouvée pour vérifier le statut d'abonnement");
          setIsSubscriptionActive(false);
          return;
        }
        
        const orgId = currentOrganization?.id || organizationData?.id;
        
        if (!orgId) {
          console.log("ID d'organisation manquant");
          setIsSubscriptionActive(false);
          return;
        }
        
        // Récupérer les données d'abonnement fraîches depuis la base de données
        const { data, error } = await supabase
          .from('organizations')
          .select('is_active, is_trial, trial_ends_at, subscription_status, subscription_plan_id')
          .eq('id', orgId)
          .single();
          
        if (error) {
          console.error("Erreur lors de la récupération du statut d'abonnement:", error);
          setIsSubscriptionActive(false);
          return;
        }
        
        if (!data) {
          console.log("Aucune donnée d'organisation trouvée");
          setIsSubscriptionActive(false);
          return;
        }
        
        console.log("Données d'abonnement récupérées:", data);
        
        // Vérifier si l'abonnement est actif
        let isActive = data.is_active;
        
        // Vérifier si l'essai est encore valide
        if (data.is_trial && data.trial_ends_at) {
          const trialEndDate = new Date(data.trial_ends_at);
          const currentDate = new Date();
          
          if (trialEndDate > currentDate) {
            // L'essai est encore valide
            isActive = true;
          }
        }
        
        // Vérifier le statut d'abonnement
        if (data.subscription_status === 'active' && data.subscription_plan_id) {
          isActive = true;
        }
        
        setIsSubscriptionActive(isActive);
        console.log("Statut d'abonnement:", isActive ? "Actif" : "Inactif");
      } catch (error) {
        console.error("Exception lors de la vérification du statut d'abonnement:", error);
        setIsSubscriptionActive(false);
      } finally {
        setIsLoading(false);
      }
    };
    
    checkSubscriptionStatus();
  }, [currentOrganization, organizationData]);

  // Function to redirect to organization settings with subscription tab active
  const redirectToPayment = () => {
    toast({
      title: "Abonnement requis",
      description: "Vous allez être redirigé vers les paramètres d'abonnement",
    });
    
    // Redirect to organization settings with subscription tab active
    console.log("Redirection vers les paramètres d'abonnement...");
    navigate('/dashboard/settings', { state: { activeMenu: 'subscription' } });
  };

  return { isSubscriptionActive, isLoading, redirectToPayment };
}
